import { ActionTypes } from "./diagnosis.actions";
import { Action } from "@ngrx/store";
import {
  Diagnosis360,
  DiagnosisItem,
  FacialOptions,
  ItemOptions,
  MalocclusionsOptions,
  TmjOptions,
} from "./diagnosis.model";
import { ActionTypes as OdontogramActionTypes } from "../../odontogram/odontogram.actions";
import { ActionTypes as PlanEditorActionTypes } from "../plan-editor.actions";

export interface State {
  currentGroupIdentifier: string;
  currentConditionIdentifier: string;
  currentItem: DiagnosisItem;
  items: DiagnosisItem[];
  showItemList: boolean;
  useOdontogram: boolean;
  mainComponent: string;
  saveAction: string;
  diagnosis360: Diagnosis360;
  orthoMalocclusions: MalocclusionsOptions;
  orthoFacial: FacialOptions;
  orthoTMJ: TmjOptions;
  loading: boolean;
}

export const initialState: State = {
  currentGroupIdentifier: "caries",
  currentConditionIdentifier: null,
  currentItem: new DiagnosisItem(),
  items: [],
  showItemList: false,
  useOdontogram: true,
  mainComponent: "odontogram",
  saveAction: null,
  diagnosis360: null,
  orthoMalocclusions: new MalocclusionsOptions(),
  orthoFacial: new FacialOptions(),
  orthoTMJ: new TmjOptions(),
  loading: false,
};

export function reducer(state: State = initialState, action: Action): State {
  let orthoItem;

  switch (action.type) {
    case ActionTypes.SELECT_GROUP:
      return Object.assign({}, state, {
        currentGroupIdentifier: action.payload.identifier,
        useOdontogram: action.payload.useOdontogram,
        mainComponent: "odontogram",
      });

    case ActionTypes.SELECT_ITEM:
      const updatedCurrentItem = Object.assign({}, state.currentItem, {
        condition: action.payload.identifier,
        options: new ItemOptions(),
      });
      return Object.assign({}, state, {
        currentConditionIdentifier: action.payload.identifier,
        currentItem: updatedCurrentItem,
        mainComponent: action.payload.mainComponent,
        saveAction: action.payload.saveAction,
      });

    case ActionTypes.UPDATE_OPTIONS:
      const updatedCurrentItem2 = Object.assign({}, state.currentItem, {
        options: Object.assign({}, state.currentItem.options, action.payload),
      });
      return Object.assign({}, state, { currentItem: updatedCurrentItem2 });

    case ActionTypes.ADD_ITEM:
      return Object.assign({}, state, { loading: true });

    case ActionTypes.ADD_ITEM_SUCCESS:
      return Object.assign({}, state, {
        items: [...state.items, action.payload],
        currentItem: new DiagnosisItem(),
        currentConditionIdentifier: null,
        loading: false,
      });

    case ActionTypes.ADD_ITEM_FAIL:
      return Object.assign({}, state, { loading: false });

    case ActionTypes.SAVE_ORTHO_ITEM:
      return Object.assign({}, state, { loading: true });

    case ActionTypes.SAVE_ORTHO_ITEM_SUCCESS:
      switch (action.payload.condition) {
        case "malocclusions":
          orthoItem = { orthoMalocclusions: action.payload.options };
          break;
        case "facial":
          orthoItem = { orthoFacial: action.payload.options };
          break;
        case "tmj":
          orthoItem = { orthoTMJ: action.payload.options };
          break;
      }
      return Object.assign({}, state, orthoItem, {
        currentConditionIdentifier: null,
        currentItem: new DiagnosisItem(),
        useOdontogram: false,
        mainComponent: "odontogram",
        loading: false,
      });

    case ActionTypes.SAVE_ORTHO_ITEM_FAIL:
      return Object.assign({}, state, { loading: false });

    case ActionTypes.DELETE_ORTHO_ITEM:
      return Object.assign({}, state, { loading: true });

    case ActionTypes.DELETE_ORTHO_ITEM_SUCCESS:
      switch (action.payload.condition) {
        case "malocclusions":
          orthoItem = { orthoMalocclusions: new MalocclusionsOptions() };
          break;
        case "facial":
          orthoItem = { orthoFacial: new FacialOptions() };
          break;
        case "tmj":
          orthoItem = { orthoTMJ: new TmjOptions() };
          break;
      }
      return Object.assign({}, state, orthoItem, { loading: false });

    case ActionTypes.DELETE_ORTHO_ITEM_FAIL:
      return Object.assign({}, state, { loading: false });

    case ActionTypes.UPDATE_ITEM_SUCCESS:
      return Object.assign({}, state, {
        items: state.items.map((item) => {
          return item.id === action.payload.id
            ? Object.assign({}, item, action.payload)
            : item;
        }),
      });

    case ActionTypes.DELETE_ITEM_SUCCESS:
      return Object.assign({}, state, {
        items: state.items.filter((item) => item.id !== action.payload.id),
      });

    case ActionTypes.SORT_ITEMS_SUCCESS:
      return Object.assign({}, state, { items: [...action.payload] });
    /*
        case ActionTypes.LOAD_ITEMS_SUCCESS:
            return Object.assign({}, state, {items: action.payload});
        */

    case ActionTypes.SHOW_LIST:
      return Object.assign({}, state, { showItemList: true });

    case ActionTypes.HIDE_LIST:
      return Object.assign({}, state, { showItemList: false });

    case ActionTypes.UPDATE_360_SUCCESS:
      return Object.assign({}, state, { diagnosis360: action.payload });

    case OdontogramActionTypes.TEETH_SELECTION_UPDATED:
      const currentItem = Object.assign({}, state.currentItem, {
        teeth: action.payload,
      });
      return Object.assign({}, state, { currentItem: currentItem });

    case PlanEditorActionTypes.LOAD_SUCCESS:
      return Object.assign({}, state, {
        items: action.payload.diagnosis.items,
        diagnosis360: action.payload.diagnosis.diagnosis360,
        orthoMalocclusions:
          Object.keys(action.payload.diagnosis.orthoMalocclusions).length > 0
            ? action.payload.diagnosis.orthoMalocclusions
            : new MalocclusionsOptions(),
        orthoFacial:
          Object.keys(action.payload.diagnosis.orthoFacial).length > 0
            ? action.payload.diagnosis.orthoFacial
            : new FacialOptions(),
        orthoTMJ:
          Object.keys(action.payload.diagnosis.orthoTMJ).length > 0
            ? action.payload.diagnosis.orthoTMJ
            : new TmjOptions(),
      });

    case PlanEditorActionTypes.CHANGE_STEP:
      return Object.assign({}, state, {
        currentGroupIdentifier: "caries",
        currentConditionIdentifier: null,
        currentItem: new DiagnosisItem(),
        showItemList: false,
        useOdontogram: true,
        mainComponent: "odontogram",
        saveAction: null,
      });

    default:
      return state;
  }
}

export const getCurrentGroupIdentifier = (state: State) =>
  state.currentGroupIdentifier;
export const getCurrentConditionIdentifier = (state: State) =>
  state.currentConditionIdentifier;
export const getCurrentItem = (state: State) => state.currentItem;
export const getItems = (state: State) => state.items;
export const getShowItemList = (state: State) => state.showItemList;
export const getUseOdontogram = (state: State) => state.useOdontogram;
export const getMainComponent = (state: State) => state.mainComponent;
export const getSaveAction = (state: State) => state.saveAction;
export const getDiagnosis360 = (state: State) => state.diagnosis360;
export const getOrthoMalocclusions = (state: State) => state.orthoMalocclusions;
export const getOrthoFacial = (state: State) => state.orthoFacial;
export const getOrthoTMJ = (state: State) => state.orthoTMJ;
export const getIsLoading = (state: State) => state.loading;



// WEBPACK FOOTER //
// /app/src/app/core/store/plan-editor/diagnosis/diagnosis.reducer.ts